/**
 * Athlete scope: which records a person sees under Mine vs Everyone.
 *
 * "Mine" is defined by athlete ownership, not by who created a row. An
 * agent's deals are the deals on their athletes (plus deals they own
 * themselves); their calendar is their athletes' games, deadlines and
 * birthdays. The same three ownership fields TheBook and Prospects use for
 * their Mine count (agent_owner_id, assigned_agent_id, owner_id) decide it.
 *
 * Rows with no athlete on them at all (an agency meeting, a league date)
 * are always in scope: scoping hides other agents' athletes, not the
 * agency's shared calendar. Any mode other than 'my-work' passes everything.
 */

import type { WorkViewMode } from '../types/agencyTeam'

export type Ownable = {
  agent_owner_id?: string | null
  assigned_agent_id?: string | null
  owner_id?: string | null
}

export type ScopedRow = { client_id?: string | null }

export type ScopedDeal = ScopedRow & {
  user_id?: string | null
  assigned_agent_id?: string | null
}

export type ScopedEvent = ScopedRow & {
  client_ids?: string[] | null
  related_entity_type?: string | null
  related_entity_id?: string | null
  external_id?: string | null
}

export type AthleteScope = {
  mode: WorkViewMode
  /** Empty unless mode is 'my-work'. */
  myClientIds: Set<string>
  /** True when the athlete is in scope; a missing id counts as "no athlete" and passes. */
  includesClient(id: string | null | undefined): boolean
  includesRow(row: ScopedRow): boolean
  /** A deal on my athlete, or a deal I own. */
  includesDeal(deal: ScopedDeal): boolean
  /** A calendar row that names my athlete by any of the ways the app links one. */
  includesEvent(event: ScopedEvent): boolean
}

export function isOwnedBy(userId: string, row: Ownable): boolean {
  if (!userId) return false
  return row.agent_owner_id === userId || row.assigned_agent_id === userId || row.owner_id === userId
}

export function myClientIdSet(userId: string, clients: (Ownable & { id: string })[]): Set<string> {
  const ids = new Set<string>()
  for (const c of clients) if (isOwnedBy(userId, c)) ids.add(c.id)
  return ids
}

/** The client id a birthday row is about, or null for any other row. */
export function birthdayClientId(externalId: string | null | undefined): string | null {
  if (!externalId) return null
  const m = /^birthday:client:([^:]+):/.exec(externalId)
  return m ? m[1] : null
}

/** Does this event name any athlete at all? Rows that do not are shared. */
export function eventNamesAthlete(event: ScopedEvent): boolean {
  return Boolean(
    event.client_id
    || (event.client_ids && event.client_ids.length > 0)
    || (event.related_entity_type === 'client' && event.related_entity_id)
    || birthdayClientId(event.external_id),
  )
}

export function buildAthleteScope(args: {
  userId: string | null | undefined
  mode: WorkViewMode
  clients: (Ownable & { id: string })[]
}): AthleteScope {
  const { mode } = args
  const everything = mode !== 'my-work'
  const myClientIds = everything || !args.userId ? new Set<string>() : myClientIdSet(args.userId, args.clients)

  const includesClient = (id: string | null | undefined): boolean => {
    if (everything) return true
    if (!id) return true
    return myClientIds.has(id)
  }

  const includesRow = (row: ScopedRow): boolean => includesClient(row.client_id)

  const includesDeal = (deal: ScopedDeal): boolean => {
    if (everything) return true
    if (deal.client_id && myClientIds.has(deal.client_id)) return true
    if (args.userId && (deal.user_id === args.userId || deal.assigned_agent_id === args.userId)) return true
    // A deal with no athlete and no owner has nobody to be scoped to; it is
    // an agency deal and shows.
    return !deal.client_id && !deal.user_id && !deal.assigned_agent_id
  }

  const includesEvent = (event: ScopedEvent): boolean => {
    if (everything) return true
    if (!eventNamesAthlete(event)) return true
    if (event.client_id && myClientIds.has(event.client_id)) return true
    if (event.client_ids?.some(id => myClientIds.has(id))) return true
    if (event.related_entity_type === 'client' && event.related_entity_id && myClientIds.has(event.related_entity_id)) return true
    const bday = birthdayClientId(event.external_id)
    if (bday && myClientIds.has(bday)) return true
    return false
  }

  return { mode, myClientIds, includesClient, includesRow, includesDeal, includesEvent }
}

/** Modes a role may use. Assistants and read-only users own nothing, so they read the agency. */
export function viewModesForRole(role: string | null | undefined): WorkViewMode[] {
  switch (role) {
    case 'superadmin': return ['my-work', 'team', 'all']
    case 'admin': return ['my-work', 'team']
    case 'agent': return ['my-work']
    case 'assistant':
    case 'read only': return ['team']
    default: return ['my-work']
  }
}
